var _dec, _dec2, _class, _TextArea;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Fragment, Component } from 'react';
import { FormField } from '@instructure/ui-form-field';
import { addEventListener, isActiveElement, requestAnimationFrame, getBoundingClientRect } from '@instructure/ui-dom-utils';
import { debounce } from '@instructure/debounce';
import { withStyle } from '@instructure/emotion';
import { px } from '@instructure/ui-utils';
import { omitProps, pickProps, withDeterministicId } from '@instructure/ui-react-utils';
import { hasVisibleChildren } from '@instructure/ui-a11y-utils';
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
import { allowedProps } from "./props.js";
/**
---
category: components
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
let TextArea = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec(_class = _dec2(_class = (_TextArea = class TextArea extends Component {
  constructor(props) {
    super(props);
    this._listener = void 0;
    this._request = void 0;
    this._defaultId = void 0;
    this._textareaResizeListener = void 0;
    this._debounced = void 0;
    this._textarea = null;
    this._container = null;
    this._height = void 0;
    this._manuallyResized = false;
    this._highlightRef = null;
    this.myObserver = null;
    this.resizeTimeout = void 0;
    this.ref = null;
    this._textareaResize = () => {
      const textareaHeight = this._textarea.style.height;
      if (textareaHeight !== '' && textareaHeight !== this._height) {
        this._manuallyResized = true;
        this._textarea.style.overflowY = 'auto';

        // update container minHeight to ensure focus ring always wraps input
        this._container.style.minHeight = textareaHeight;
      }
    };
    this.grow = () => {
      if (!this._textarea || this._manuallyResized) {
        return;
      }
      const offset = this._textarea.offsetHeight - this._textarea.clientHeight;
      let height = '';

      // Notes:
      // 1. height has to be reset to `auto` every time this method runs, or scrollHeight will not reset
      // 2. `this._textarea.scrollHeight` will not reset if assigned to a variable; it needs to be written out each time
      this._textarea.style.height = 'auto';
      this._textarea.style.overflowY = 'hidden'; // hide scrollbars for autoGrow textareas
      height = this._textarea.scrollHeight + offset + 'px';
      const maxHeight = this.props.maxHeight ? px(this.props.maxHeight, this._container) : void 0;
      if (this.props.maxHeight && maxHeight !== void 0 && this._textarea.scrollHeight > maxHeight) {
        this._textarea.style.overflowY = 'auto'; // add scroll if scrollHeight exceeds maxHeight in pixels
      } else if (this.props.height) {
        if (this._textarea.value === '') {
          height = this.props.height;
        } else if (px(this.props.height, this._container) > this._textarea.scrollHeight) {
          this._textarea.style.overflowY = 'auto'; // add scroll if scrollHeight exceeds height in pixels
          height = this.props.height;
        }
      }

      // preserve container height to prevent scroll jumping on long textareas,
      // but make sure container doesn't exceed maxHeight prop
      const heightExceedsMax = maxHeight !== void 0 && px(height) > maxHeight;
      if (!heightExceedsMax) {
        this._container.style.minHeight = height;
      }
      this._height = height;
      this._textarea.style.height = height;
    };
    this.handleChange = event => {
      const _this$props = this.props,
        onChange = _this$props.onChange,
        value = _this$props.value,
        disabled = _this$props.disabled,
        readOnly = _this$props.readOnly;
      if (disabled || readOnly) {
        event.preventDefault();
        return;
      }
      if (typeof value === 'undefined') {
        // if uncontrolled
        this.autoGrow();
      }
      if (typeof onChange === 'function') {
        onChange(event);
      }
    };
    this.handleContainerRef = node => {
      this._container = node;
    };
    this._defaultId = props.deterministicId();

    //mock ResizeObserver for ssr
    if (typeof window === 'undefined') {
      global.ResizeObserver = class ResizeObserver {
        observe() {
          // do nothing
        }
        unobserve() {
          // do nothing
        }
        disconnect() {
          // do nothing
        }
      };
    }
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props2;
    this.myObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (this._highlightRef) {
          const entryStyle = window.getComputedStyle(entry.target);
          this._highlightRef.style.transition = 'none';
          this._highlightRef.style.width = `calc(${entryStyle.width} + 0.5rem)`;
          this._highlightRef.style.height = `calc(${entryStyle.height} + 0.5rem)`;
          clearTimeout(this.resizeTimeout);
          this.resizeTimeout = setTimeout(() => {
            if (this._highlightRef) {
              this._highlightRef.style.transition = 'all 0.2s';
            }
          }, 500);
        }
      }
    });
    this.autoGrow();
    (_this$props$makeStyle = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props2);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props3;
    this.autoGrow();
    (_this$props$makeStyle2 = (_this$props3 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props3);
  }
  componentWillUnmount() {
    if (this._listener) {
      this._listener.remove();
    }
    if (this._textareaResizeListener) {
      this._textareaResizeListener.disconnect();
    }
    if (this._request) {
      this._request.cancel();
    }
    if (this._debounced) {
      this._debounced.cancel();
    }
    if (this.myObserver) {
      this.myObserver.disconnect();
    }
  }
  autoGrow() {
    if (this.props.autoGrow) {
      if (!this._debounced) {
        this._debounced = debounce(this.grow, 200, {
          leading: false,
          trailing: true
        });
      }
      if (!this._listener) {
        this._listener = addEventListener(window, 'resize', this._debounced);
      }
      if (this._textarea && !this._textareaResizeListener) {
        const _getBoundingClientRec = getBoundingClientRect(this._textarea),
          origHeight = _getBoundingClientRec.height;
        this._textareaResizeListener = new ResizeObserver(entries => {
          for (const entry of entries) {
            const height = entry.contentRect.height;
            if (origHeight !== height) {
              this._textareaResize();
            }
          }
        });
        this._textareaResizeListener.observe(this._textarea);
      }
      this._request = requestAnimationFrame(this.grow);
    }
  }
  focus() {
    this._textarea.focus();
  }
  get minHeight() {
    return this._textarea.style.minHeight;
  }
  get invalid() {
    return this.props.messages && this.props.messages.findIndex(message => {
      return message.type === 'error' || message.type === 'newError';
    }) >= 0;
  }
  get id() {
    return this.props.id || this._defaultId;
  }
  get focused() {
    return isActiveElement(this._textarea);
  }
  get value() {
    return this._textarea.value;
  }
  render() {
    var _this$props$styles, _this$props$styles2, _this$props$styles3;
    const _this$props4 = this.props,
      autoGrow = _this$props4.autoGrow,
      placeholder = _this$props4.placeholder,
      value = _this$props4.value,
      defaultValue = _this$props4.defaultValue,
      disabled = _this$props4.disabled,
      readOnly = _this$props4.readOnly,
      required = _this$props4.required,
      width = _this$props4.width,
      height = _this$props4.height,
      maxHeight = _this$props4.maxHeight,
      textareaRef = _this$props4.textareaRef,
      resize = _this$props4.resize,
      styles = _this$props4.styles,
      margin = _this$props4.margin;
    const props = omitProps(this.props, TextArea.allowedProps);
    const style = {
      width,
      resize,
      height: !autoGrow ? height : void 0,
      maxHeight
    };
    const textarea = _jsx("textarea", {
      ...props,
      value: value,
      defaultValue: defaultValue,
      placeholder: placeholder,
      ref: textarea => {
        if (textarea) {
          var _this$myObserver;
          (_this$myObserver = this.myObserver) === null || _this$myObserver === void 0 ? void 0 : _this$myObserver.observe(textarea);
        }
        this._textarea = textarea;
        if (typeof textareaRef === 'function') {
          textareaRef(textarea);
        }
      },
      style: style,
      id: this.id,
      required: required,
      "aria-required": required,
      "aria-invalid": this.invalid ? 'true' : void 0,
      disabled: disabled || readOnly,
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.textArea,
      onChange: this.handleChange
    });
    const label = hasVisibleChildren(this.props.label) ? _jsxs(Fragment, {
      children: [this.props.label, required && _jsxs("span", {
        css: this.invalid ? styles === null || styles === void 0 ? void 0 : styles.requiredInvalid : {},
        "aria-hidden": true,
        children: [' ', "*"]
      })]
    }) : this.props.label;
    return _jsx(FormField, {
      ...pickProps(this.props, FormField.allowedProps),
      label: label,
      vAlign: "top",
      id: this.id,
      elementRef: el => {
        this.ref = el;
      },
      margin: margin,
      "data-cid": "TextArea",
      children: _jsxs("div", {
        css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.textAreaLayout,
        style: {
          width,
          maxHeight
        },
        ref: this.handleContainerRef,
        children: [textarea, !disabled && !readOnly ? _jsx("span", {
          css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.textAreaOutline,
          "aria-hidden": "true",
          ref: e => {
            this._highlightRef = e;
          }
        }) : null]
      })
    });
  }
}, _TextArea.displayName = "TextArea", _TextArea.componentId = 'TextArea', _TextArea.allowedProps = allowedProps, _TextArea.defaultProps = {
  size: 'medium',
  autoGrow: true,
  resize: 'none',
  inline: false,
  messages: [],
  disabled: false,
  readOnly: false,
  layout: 'stacked',
  required: false
}, _TextArea)) || _class) || _class);
export default TextArea;
export { TextArea };